import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowSquareOutIcon, CaretDownIcon, CheckCircleIcon, WarningIcon } from '@phosphor-icons/react';
import { Button, Drawer, Loading } from 'barte-design-system';
import { parseISO } from 'date-fns';

import { useGetTerminalDetails, POS_CHECKLIST_URL } from '@/modules/backoffice/services/terminals';
import { TerminalStatus } from '@/modules/backoffice/types/terminal';
import type { IAllocationHistoryItem, TerminalRecordStatus } from '@/modules/backoffice/types/terminal';
import { getTerminalStatusLabel } from '@/modules/backoffice/helpers/get-terminal-status-label';
import {
  formatDateTimeToDisplay,
  formatDateOnlyToDisplay,
  formatMonthYearToDisplay,
  getFirstWord,
} from '@/modules/shared/helpers/formatters';
import { OperationCard } from '../operation-card';
import {
  getDetailsOperationsForStatus,
} from '../drawer/batch-unlink-drawer/batch-unlink-drawer.utils';
import styles from './terminal-details-drawer.module.css';
import type {
  TerminalDetailsDrawerProps,
  TimelineEvent,
  FieldRowProps,
} from './terminal-details-drawer.type';


export const buildTimelineEvents = (
  history: IAllocationHistoryItem[],
  t: (key: string) => string,
): TimelineEvent[] => {
  const events: TimelineEvent[] = [];

  history.forEach((item) => {
    if (item.linkedAt) {
      events.push({
        date: item.linkedAt,
        title: t('backoffice.terminalHistoryLinked'),
        company: getFirstWord(item.payfacName || item.sellerName),
        monthYear: formatMonthYearToDisplay(item.linkedAt),
        formattedDate: formatDateTimeToDisplay(item.linkedAt) ?? item.linkedAt,
      });
    }
    if (item.unlinkedAt) {
      events.push({
        date: item.unlinkedAt,
        title: t('backoffice.terminalHistoryUnlinked'),
        company: getFirstWord(item.payfacName || item.sellerName),
        monthYear: formatMonthYearToDisplay(item.unlinkedAt),
        formattedDate: formatDateTimeToDisplay(item.unlinkedAt) ?? item.unlinkedAt,
      });
    }
  });

  events.sort((a, b) => {
    try {
      return parseISO(b.date).getTime() - parseISO(a.date).getTime();
    } catch {
      return 0;
    }
  });

  return events;
};

type CollapsibleSectionProps = {
  title: string;
  children: React.ReactNode;
};

const CollapsibleSection = ({ title, children }: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className={styles.section}>
      <button
        className={styles['section-header']}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <p className={styles['section-title']}>{title}</p>
        <CaretDownIcon
          size={16}
          className={`${styles['section-toggle-icon']} ${isOpen ? styles['section-toggle-icon--open'] : ''}`}
        />
      </button>
      {isOpen && children}
    </div>
  );
};

const getStatusBadgeClass = (status: TerminalRecordStatus): string => {
  const classMap: Record<TerminalRecordStatus, string> = {
    [TerminalStatus.AVAILABLE]: styles['status-badge--available'],
    [TerminalStatus.LINKED]: styles['status-badge--linked'],
    [TerminalStatus.ACTIVE]: styles['status-badge--active'],
    [TerminalStatus.INACTIVE]: styles['status-badge--inactive'],
    [TerminalStatus.TEST]: styles['status-badge--test'],
    [TerminalStatus.SUPPORT]: styles['status-badge--support'],
  };
  return classMap[status] ?? '';
};

type AllocationTimelineProps = {
  events: TimelineEvent[];
};

const AllocationTimeline = ({ events }: AllocationTimelineProps) => (
  <div className={styles.timeline}>
    {events.map((event, index) => {
      const isLatest = index === 0;
      const showBadge =
        index === 0 || event.monthYear !== events[index - 1].monthYear;

      return (
        <div key={`${event.date}-${event.title}`} className={styles['timeline-item']}>
          <div className={styles['timeline-date']}>
            {showBadge && (
              <span className={styles['timeline-date-badge']}>
                {event.monthYear}
              </span>
            )}
          </div>
          <div className={styles['timeline-connector']}>
            <div
              className={`${styles['timeline-dot']} ${
                isLatest
                  ? styles['timeline-dot--latest']
                  : styles['timeline-dot--past']
              }`}
            />
            {index < events.length - 1 && (
              <div className={styles['timeline-line']} />
            )}
          </div>
          <div className={styles['timeline-content']}>
            <p className={`${styles['timeline-title']} ${isLatest ? styles['timeline-title--latest'] : styles['timeline-title--past']}`}>
              {event.title}
            </p>
            <p className={`${styles['timeline-description']} ${isLatest ? styles['timeline-description--latest'] : styles['timeline-description--past']}`}>
              {event.formattedDate}
              {event.company && (
                <> &bull; <strong>{event.company}</strong></>
              )}
            </p>
          </div>
        </div>
      );
    })}
  </div>
);

const FieldRow = ({ label, value, subValue, badgeClass }: FieldRowProps) => (
  <div className={styles['field-row']}>
    <p className={styles['field-label']}>{label}</p>
    {value ? (
      badgeClass ? (
        <span className={`${styles['status-badge']} ${badgeClass}`}>{value}</span>
      ) : subValue ? (
        <div className={styles['field-value-two-line']}>
          <p className={styles['field-value']}>{value}</p>
          <p className={styles['field-value-sub']}>{subValue}</p>
        </div>
      ) : (
        <p className={styles['field-value']}>{value}</p>
      )
    ) : (
      <p className={styles['field-value-empty']}>—</p>
    )}
  </div>
);

export const TerminalDetailsDrawer = ({
  isOpen,
  serialNumber,
  onClose,
  onOpenSuccessModal,
  onOpenFailureModal,
  onStartOperation,
}: TerminalDetailsDrawerProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetTerminalDetails(serialNumber);

  const terminal = data?.data;
  const isTest = terminal?.status === TerminalStatus.TEST;
  const isSupport = terminal?.status === TerminalStatus.SUPPORT;
  const terminalStatus = terminal?.status;
  const operations = useMemo(
    () => (terminalStatus ? getDetailsOperationsForStatus(terminalStatus) : []),
    [terminalStatus],
  );

  const timelineEvents = useMemo(
    () => buildTimelineEvents(terminal?.allocationHistory ?? [], t),
    [terminal?.allocationHistory, t],
  );

  return (
    <Drawer
      title={serialNumber}
      isOpen={isOpen}
      onClose={onClose}
      className={styles['drawer-wide']}
    >
      {isLoading && (
        <div className={styles['loading-wrapper']}>
          <Loading type="page" />
        </div>
      )}

      {!isLoading && serialNumber && !terminal && (
        <p className={styles['error-message']}>
          {t('backoffice.terminalTestDrawerLoadError')}
        </p>
      )}

      {!isLoading && terminal && (
        <div className={styles.content}>
          {isTest && (
            <div className={styles.section}>
              <p className={styles['section-title']}>
                {t('backoffice.terminalDetailsTestResultTitle')}
              </p>
              <div className={styles['operation-cards']}>
                <OperationCard
                  icon={<CheckCircleIcon size={24} weight="fill" />}
                  labelKey="backoffice.terminalDetailsTestApproveLabel"
                  hideDescription
                  onClick={onOpenSuccessModal}
                />
                <OperationCard
                  icon={<WarningIcon size={24} weight="fill" />}
                  labelKey="backoffice.terminalDetailsTestFailLabel"
                  hideDescription
                  onClick={onOpenFailureModal}
                />
              </div>
            </div>
          )}

          {operations.length > 0 && (
            <div className={styles.section}>
              <p className={styles['section-title']}>
                {t('backoffice.terminalDetailsOperationsTitle', { count: operations.length })}
              </p>
              <div className={styles['operation-cards']}>
                {operations.map((option) => (
                  <OperationCard
                    key={option.type}
                    icon={<option.Icon />}
                    labelKey={option.labelKey}
                    hideDescription={option.hideDescription}
                    onClick={() => onStartOperation(option.type)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={styles.section}>
            <p className={styles['section-title']}>
              {t('backoffice.terminalTestDrawerSectionGeneral')}
            </p>
            <div className={styles.card}>
              <FieldRow
                label={t('backoffice.terminalTestDrawerFieldSerialNumber')}
                value={terminal.serialNumber}
              />
              <FieldRow
                label={t('backoffice.terminalTestDrawerFieldStatus')}
                value={getTerminalStatusLabel(terminal.status, t)}
                badgeClass={getStatusBadgeClass(terminal.status)}
              />
              {terminal.posVersion && (
                <FieldRow
                  label={t('backoffice.terminalTestDrawerFieldPosVersion')}
                  value={terminal.posVersion}
                />
              )}
              <FieldRow
                label={t('backoffice.terminalTestDrawerFieldPhoebusId')}
                value={terminal.phoebusId}
              />
              <FieldRow
                label={t('backoffice.terminalTestDrawerFieldCreatedAt')}
                value={formatDateOnlyToDisplay(terminal.createdAt)}
              />
            </div>
          </div>

          {terminal.currentLink && (
            <div className={styles.section}>
              <p className={styles['section-title']}>
                {t('backoffice.terminalTestDrawerSectionBinding')}
              </p>
              <div className={styles.card}>
                <FieldRow
                  label={t('backoffice.terminalTestDrawerFieldIntermediary')}
                  value={terminal.currentLink.payfacName}
                  subValue={terminal.currentLink.payfacExternalId}
                />
                <FieldRow
                  label={t('backoffice.terminalTestDrawerFieldSeller')}
                  value={terminal.currentLink.sellerName}
                  subValue={terminal.currentLink.sellerExternalId}
                />
                <FieldRow
                  label={t('backoffice.terminalTestDrawerFieldLinkedAt')}
                  value={formatDateOnlyToDisplay(terminal.currentLink.linkedAt)}
                />
                <FieldRow
                  label={t('backoffice.terminalTestDrawerFieldLastTransaction')}
                  value={formatDateTimeToDisplay(terminal.currentLink.lastTransactionAt)}
                />
              </div>
            </div>
          )}

          {!isTest && (
            <CollapsibleSection title={t('backoffice.terminalTestDrawerSectionHistory')}>
              {timelineEvents.length === 0 ? (
                <p className={styles['history-empty']}>{t('backoffice.terminalTestDrawerHistoryEmpty')}</p>
              ) : (
                <AllocationTimeline events={timelineEvents} />
              )}
            </CollapsibleSection>
          )}

          <div className={styles.section}>
            <p className={styles['section-title']}>
              {t('backoffice.terminalTestDrawerSectionChecklist')}
            </p>
            <p className={styles['checklist-description']}>
              {t('backoffice.terminalTestDrawerChecklistDescription')}
            </p>
            <a
              href={POS_CHECKLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles['checklist-link']}
            >
              {t('backoffice.terminalTestDrawerChecklistLink')}
              <ArrowSquareOutIcon size={16} />
            </a>

            {isSupport && (
              <div className={styles['action-buttons']}>
                <Button
                  onClick={onOpenSuccessModal}
                  iconLeft={<CheckCircleIcon size={16} />}
                >
                  {t('backoffice.terminalSupportDrawerChangeStatusButton')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};
